import { Alert, Box, Button, LinearProgress, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useNotification } from '../../../hooks/NotificationContext';
import { addTestCases, generateAITestCase, generateAITestCasesFromStory } from '../../../modules/jira';
import { getProjectAIAccess } from '../../../modules/aiConfig';
import { addTestSteps } from '../../../modules/teststep';
import ConfirmationModal from '../../elements/ConfirmationModal';
import Modal from '../../elements/Modal';
import CreateTestCaseForm from './CreateTestCaseForm';
import CreateTestCaseChoiceScreen from './CreateTestCaseChoiceScreen';
import JiraStorySelector from './JiraStorySelector';
import JiraReviewScreen from './JiraReviewScreen';
import InMemoryStepsModal from './InMemoryStepsModal';
import AiInputScreen from './AiInputScreen';
import AiSuggestions from './AiSuggestions';
import { defaultFormData, SAVE_PHASE, SCREEN } from '../test-cycles/static-data';
import { useBulkSave } from './Usebulksave';
import { getCurrentProject } from '../../../modules/project';
import QUERY_KEYS from '../../../query/queryKeys';

const normalizeStep = ({ step, testData, expectedResult, uri }) => ({
  id: null,
  step: step || '',
  testData: testData || '',
  expectedResult: expectedResult || '',
  uri: uri || '',
});

const mapApiTcToInternal = (tc, idx, testsuites) => ({
  ...defaultFormData,
  _id: idx,
  selected: false,
  summary: tc.summary || '',
  objective: tc.objective || '',
  preCondition: tc.pre_condition || '',
  module: tc.module || null,
  labels: tc.labels || [],
  technologyLayer: tc.technology_layer || '',
  coverageTag: tc.coverage_tag || 'Happy Path',
  testsuites,
  testSteps: (tc.test_steps || []).map((s) => ({
    step: s.step || '',
    testData: s.test_data || '',
    expectedResult: s.expected_result || '',
    uri: '',
  })),
  stepsExpanded: false,
  _crossMatchKey: tc.cross_match_key ?? null,
  _crossMatchScore: tc.cross_match_score ?? null,
  _crossMatchStrength: tc.cross_match_strength ?? null,
});

const GeneratingOverlay = ({ subtitle }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, gap: 3, minHeight: 260 }}>
    <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'primary.50', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid', borderColor: 'primary.200' }}>
      <AutoAwesomeIcon sx={{ fontSize: 28, color: 'primary.main' }} />
    </Box>
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>Generating test cases…</Typography>
      {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
    </Box>
    <Box sx={{ width: '100%', maxWidth: 320 }}>
      <LinearProgress sx={{ height: 6, borderRadius: 3 }} />
    </Box>
    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
      Please don't close this window
    </Typography>
  </Box>
);

const AddTestCaseModal = ({
  showModal,
  handleClose,
  statuses,
  priorities,
  modules,
  labels,
  testSuites,
  projectUsers,
  loading,
  projectId,
  isReviewWorkflow,
  refetchData,
  technologyLayers,
}) => {
  const navigate = useNavigate();
  const { addNotification } = useNotification();

  const currentUserId = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user'))?.id ?? null; }
    catch { return null; }
  }, []);

  const [screen, setScreen] = useState(SCREEN.CHOICE);
  const [formData, setFormData] = useState(defaultFormData);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmContext, setConfirmContext] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [aiDescription, setAiDescription] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiMode, setAiMode] = useState('multiple');
  const [suggestedLabels, setSuggestedLabels] = useState([]);
  const [suggestedModule, setSuggestedModule] = useState(null);
  const [suggestedTechnologyLayer, setSuggestedTechnologyLayer] = useState(null);
  const [aiTestSteps, setAiTestSteps] = useState([]);
  const [showStepsModal, setShowStepsModal] = useState(false);
  const [reviewSource, setReviewSource] = useState(SCREEN.JIRA_INPUT);
  const [selectedStory, setSelectedStory] = useState(null);
  const [jiraTestSuite, setJiraTestSuite] = useState([]);
  const [jiraGenerating, setJiraGenerating] = useState(false);
  const [jiraGenerateError, setJiraGenerateError] = useState('');
  const [jiraReviewTCs, setJiraReviewTCs] = useState([]);
  const [editingTcId, setEditingTcId] = useState(null);
  const [existingMatches, setExistingMatches] = useState([]);
  const [selectedExisting, setSelectedExisting] = useState(new Set());
  const [tokenUsage, setTokenUsage] = useState(null);


  const { data: currentProject } = useQuery({
    queryKey: QUERY_KEYS.project.current(projectId),
    queryFn: () => getCurrentProject(projectId),
    enabled: !!projectId,
  });
  const linkedJiraProjects = currentProject?.linked_jira_projects || [];

    const { data: aiAccess } = useQuery({
    queryKey: QUERY_KEYS.project.aiAccess(projectId),
    queryFn: () => getProjectAIAccess(projectId, 'testCaseGeneration'),
    enabled: !!projectId,
    staleTime: 0,
    refetchOnWindowFocus: true,
    });

    const hasAiAccess = !!aiAccess?.has_access
    const aiAccessDeniedReason = aiAccess?.message ?? null;
  const { savePhase, setSavePhase, saveProgress, saveResult, setSaveResult, handleBulkSave, resetSaveState } = useBulkSave({
    jiraReviewTCs,
    setJiraReviewTCs,
    selectedStory,
    statuses,
    priorities,
    currentUserId,
    projectUsers,
    projectId,
    refetchData,
    handleClose,
    selectedExisting,
    existingMatches,
  });

  const isAiMode = screen === SCREEN.FORM && !!aiDescription;
  const isJiraEditMode = screen === SCREEN.FORM && editingTcId !== null;
  const selectedTcs = jiraReviewTCs.filter((tc) => tc.selected);
  const selectedCount = selectedTcs.length;
  const hasUnresolvedWarnings = selectedTcs.some((tc) => !tc.technologyLayer);
  const isBulkOperationActive = savePhase === SAVE_PHASE.SAVING || savePhase === SAVE_PHASE.LINKING;
  const isOperationInFlight = jiraGenerating || aiGenerating || submitting || isBulkOperationActive;
  const currentTechLayerName = technologyLayers?.find((t) => t.id === formData.technologyLayer)?.name || '';

  const isAdmin = useMemo(() => {
    const cu = projectUsers?.find((u) => u.id === currentUserId);
    return ['admin', 'org-admin', 'org-manager'].includes(cu?.role);
  }, [projectUsers, currentUserId]);

  const filteredStatuses = useMemo(() => statuses?.filter((s) => s.name !== 'Archived') ?? [], [statuses]);

  const resetAll = useCallback(() => {
    resetSaveState();
    setScreen(SCREEN.CHOICE);
    setFormData(defaultFormData);
    setAiDescription('');
    setAiError('');
    setAiMode('multiple');
    setSuggestedLabels([]);
    setSuggestedModule(null);
    setSuggestedTechnologyLayer(null);
    setAiTestSteps([]);
    setShowStepsModal(false);
    setSelectedStory(null);
    setJiraTestSuite([]);
    setJiraGenerateError('');
    setJiraReviewTCs([]);
    setExistingMatches([]);
    setSelectedExisting(new Set());
    setEditingTcId(null);
    setConfirmContext(null);
    setTokenUsage(null);
    setReviewSource(SCREEN.JIRA_INPUT);
  }, [resetSaveState]);

  const prevShowModal = useRef(showModal);
  useEffect(() => {
    if (prevShowModal.current && !showModal) resetAll();
    prevShowModal.current = showModal;
  }, [showModal, resetAll]);

  const applyReviewResult = useCallback((result, suites, source) => {
    const tcs = (result?.test_cases || []).map((tc, idx) => mapApiTcToInternal(tc, idx, suites));
    if (tcs.length === 0) return false;
    setJiraReviewTCs(tcs);
    setTokenUsage(result?.token_usage ?? null);
    setExistingMatches(result?.existing_matches || []);
    setSelectedExisting(new Set());
    setReviewSource(source);
    setScreen(SCREEN.JIRA_REVIEW);
    return true;
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!aiDescription.trim()) return;
    setAiError('');
    setAiGenerating(true);
    try {
      if (aiMode === 'multiple') {
        const result = await generateAITestCasesFromStory({ summary: aiDescription }, projectId);
        if (!applyReviewResult(result, jiraTestSuite, SCREEN.AI_INPUT)) {
          setAiError('No test cases were generated. Please try again.');
        }
      } else {
        const result = await generateAITestCase(aiDescription, projectId);
        setFormData((prev) => ({
          ...prev,
          summary: result.summary || '',
          objective: result.objective || '',
          preCondition: result.pre_condition || '',
        }));
        setAiTestSteps(
          (result.test_steps || []).map(({ step, test_data, expected_result }) => ({
            step: step || '', testData: test_data || '', expectedResult: expected_result || '', uri: '',
          })),
        );
        setSuggestedLabels(result.labels || []);
        setSuggestedModule(result.module || null);
        setSuggestedTechnologyLayer(result.technology_layer || null);
        setScreen(SCREEN.FORM);
        setTokenUsage(result?.token_usage ?? null);
      }
    } catch (error) {
      setAiError(error.message || 'Something went wrong while generating. Please try again.');
    } finally {
      setAiGenerating(false);
    }
  }, [aiDescription, aiMode, projectId, jiraTestSuite, applyReviewResult]);

  const handleGenerateFromStory = useCallback(async () => {
    if (!selectedStory || !jiraTestSuite.length) return;
    setJiraGenerating(true);
    setJiraGenerateError('');
    try {
      const result = await generateAITestCasesFromStory(
        {
          key: selectedStory.key,
          summary: selectedStory.summary,
          description: selectedStory.description,
          issuestatus: selectedStory.issuestatus,
          priority: selectedStory.priority,
        },
        projectId,
      );
      if (!applyReviewResult(result, jiraTestSuite, SCREEN.JIRA_INPUT)) {
        setJiraGenerateError('No test cases were generated. Please try again.');
      }
    } catch (err) {
      setJiraGenerateError(err.message || 'Failed to generate test cases. Please try again.');
    } finally {
      setJiraGenerating(false);
    }
  }, [selectedStory, jiraTestSuite, projectId, applyReviewResult]);

  const handleExistingToggle = useCallback((id) => {
    setSelectedExisting((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleFormSubmit = useCallback(async () => {
    if (!formData.summary?.trim()) {
      addNotification('danger', 'Please add a summary');
      return;
    }
    setSubmitting(true);
    try {
      const testcase = {
        ...formData,
        reasonForNotAutomated: formData.isAutomated === 'No' ? formData.reasonForNotAutomated : '',
      };
      const response = await addTestCases({ testcase }, projectId);
      if (aiTestSteps.length > 0) {
        try {
          await addTestSteps({ testSteps: aiTestSteps.map(normalizeStep) }, response?.test_case?.id);
        } catch (err) {
          console.error('Failed to save test steps:', err);
        }
      }
      addNotification('success', 'Test case created successfully! Redirecting to test case steps…');
      resetAll();
      navigate(`/project/${projectId}/testcases/${response?.test_case?.key}?type=testscripts`);
    } catch (error) {
      addNotification('danger', error?.message || 'Failed to create test case. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  }, [formData, projectId, aiTestSteps, addNotification, navigate, resetAll]);

  const handleEditTc = useCallback((tc) => {
    setEditingTcId(tc._id);
    setFormData({
      ...defaultFormData,
      summary: tc.summary,
      objective: tc.objective,
      preCondition: tc.preCondition,
      module: tc.module,
      labels: tc.labels,
      technologyLayer: tc.technologyLayer,
      testsuites: tc.testsuites,
    });
    setAiTestSteps(tc.testSteps || []);
    setScreen(SCREEN.FORM);
  }, []);

  const handleSaveEditedTc = useCallback(() => {
    setJiraReviewTCs((prev) =>
      prev.map((tc) =>
        tc._id === editingTcId
          ? { ...tc, summary: formData.summary, objective: formData.objective, preCondition: formData.preCondition, module: formData.module, labels: formData.labels, technologyLayer: formData.technologyLayer, testsuites: formData.testsuites, testSteps: aiTestSteps }
          : tc,
      ),
    );
    setEditingTcId(null);
    setFormData(defaultFormData);
    setAiTestSteps([]);
    setScreen(SCREEN.JIRA_REVIEW);
  }, [editingTcId, formData, aiTestSteps]);

  const handleCloseModal = useCallback(() => {
    if (isOperationInFlight) return;
    if (screen === SCREEN.FORM && (formData.summary || formData.objective || formData.preCondition)) {
      setConfirmContext(isJiraEditMode ? 'edit' : 'form');
      setShowConfirmationModal(true);
      return;
    }
    if (screen === SCREEN.JIRA_REVIEW && jiraReviewTCs.length > 0 && savePhase === SAVE_PHASE.IDLE) {
      setConfirmContext('review');
      setShowConfirmationModal(true);
      return;
    }
    handleClose();
    resetAll();
  }, [isOperationInFlight, screen, formData.summary, formData.objective, formData.preCondition, isJiraEditMode, jiraReviewTCs.length, savePhase, handleClose, resetAll]);

  const handleDiscard = useCallback(() => {
    setShowConfirmationModal(false);
    if (confirmContext === 'edit') {
      setEditingTcId(null);
      setFormData(defaultFormData);
      setAiTestSteps([]);
      setScreen(SCREEN.JIRA_REVIEW);
    } else {
      handleClose();
      resetAll();
    }
    setConfirmContext(null);
  }, [confirmContext, handleClose, resetAll]);

  const handleAcceptLabelChip = useCallback((labelId) => {
    setFormData((prev) => ({ ...prev, labels: prev.labels.includes(labelId) ? prev.labels : [...prev.labels, labelId] }));
    setSuggestedLabels((prev) => prev.filter((id) => id !== labelId));
  }, []);

  const handleAcceptModuleChip = useCallback(() => {
    setSuggestedModule((mod) => { setFormData((prev) => ({ ...prev, module: mod })); return null; });
  }, []);

  const handleAcceptTechLayerChip = useCallback(() => {
    setSuggestedTechnologyLayer((tech) => { setFormData((prev) => ({ ...prev, technologyLayer: tech })); return null; });
  }, []);

  const disableCreate =
    !formData.summary ||
    !formData.technologyLayer ||
    formData.testsuites.length === 0 ||
    (formData.isAutomated === 'Yes' && formData.automationMethod === null) ||
    (formData.isAutomationNeeded === true && formData.isAutomated === 'No' && (!formData.notAutomatedCategory || !formData.notAutomatedSubCategory));

  const modalTitle = {
    [SCREEN.CHOICE]: 'Create Test Case',
    [SCREEN.AI_INPUT]: 'Generate Test Case with AI',
    [SCREEN.FORM]: isJiraEditMode ? 'Edit Test Case' : 'Create Test Case',
    [SCREEN.JIRA_INPUT]: 'Generate from Jira Story',
    [SCREEN.JIRA_REVIEW]: 'Review Generated Test Cases',
  }[screen];

  const confirmationBody =
    confirmContext === 'edit' ? 'Are you sure you want to cancel? Your edits will be lost.' :
      confirmContext === 'review' ? 'Are you sure you want to close? All generated test cases will be discarded.' :
        'Are you sure you want to cancel? Any unsaved changes will be lost.';

  const renderActions = () => {
    if (isOperationInFlight || savePhase === SAVE_PHASE.SUCCESS) return null;

    if (screen === SCREEN.CHOICE) {
      return <Button variant="outlined" color="secondary" onClick={() => { handleClose(); resetAll(); }}>Cancel</Button>;
    }

    if (screen === SCREEN.AI_INPUT) {
      return (
        <>
          <Button variant="outlined" color="secondary" onClick={resetAll}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<AutoAwesomeIcon />}
            onClick={handleGenerate}
            disabled={!aiDescription.trim() || (aiMode === 'multiple' && !jiraTestSuite.length)}
          >
            Generate
          </Button>
        </>
      );
    }

    if (screen === SCREEN.JIRA_INPUT) {
      return (
        <>
          <Button variant="outlined" color="secondary" onClick={resetAll}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<AutoAwesomeIcon />}
            onClick={handleGenerateFromStory}
            disabled={!selectedStory || !jiraTestSuite.length || !selectedStory.description}
          >
            Generate Test Cases
          </Button>
        </>
      );
    }

    if (screen === SCREEN.JIRA_REVIEW) {
      if (savePhase === SAVE_PHASE.PARTIAL || savePhase === SAVE_PHASE.ERROR) {
        return (
          <>
            <Button variant="outlined" color="secondary" onClick={() => { setSavePhase(SAVE_PHASE.IDLE); setSaveResult(null); }}>Retry Failed</Button>
            <Button variant="outlined" onClick={() => { refetchData(); resetAll(); handleClose(); }}>Close</Button>
          </>
        );
      }
      return (
        <>
          <Button variant="outlined" color="secondary" onClick={() => setScreen(reviewSource)}>Back</Button>
          <Button
            variant="contained"
            onClick={handleBulkSave}
            disabled={(selectedCount === 0 && selectedExisting.size === 0) || hasUnresolvedWarnings}
            title={hasUnresolvedWarnings ? 'Resolve tech layer warnings to enable save' : undefined}
          >
            Save
          </Button>
        </>
      );
    }

    return (
      <>
        <Button variant="outlined" color="secondary" disabled={submitting} onClick={handleCloseModal}>
          {isJiraEditMode ? 'Cancel' : 'Close'}
        </Button>
        <Button variant="outlined" startIcon={<FormatListBulletedIcon />} onClick={() => setShowStepsModal(true)} disabled={submitting} title={aiTestSteps.length > 0 ? 'Edit test steps' : 'Add test steps'}>
          {aiTestSteps.length > 0 ? `Steps (${aiTestSteps.length})` : 'Add Steps'}
        </Button>
        <Button variant="contained" onClick={isJiraEditMode ? handleSaveEditedTc : handleFormSubmit} disabled={disableCreate || submitting}>
          {isJiraEditMode ? 'Save Changes' : 'Create'}
        </Button>
      </>
    );
  };

  return (
    <>
      <Modal open={showModal} handleClose={handleCloseModal} modalTitle={modalTitle} action={() => renderActions()}>
       {screen === SCREEN.CHOICE && <CreateTestCaseChoiceScreen onChoice={setScreen} hasAiAccess={hasAiAccess} aiAccessDeniedReason={aiAccessDeniedReason} />}

        {screen === SCREEN.AI_INPUT && (
          aiGenerating
            ? <GeneratingOverlay subtitle="AI is generating your test cases" />
            : (
              <AiInputScreen
                aiDescription={aiDescription}
                setAiDescription={setAiDescription}
                aiGenerating={aiGenerating}
                aiError={aiError}
                mode={aiMode}
                onModeChange={setAiMode}
                testSuites={testSuites}
                selectedTestSuites={jiraTestSuite}
                onTestSuitesChange={setJiraTestSuite}
              />
            )
        )}

        {screen === SCREEN.JIRA_INPUT && (
          jiraGenerating
            ? <GeneratingOverlay subtitle={`Analysing ${selectedStory?.key} and creating test cases`} />
            : (
              <JiraStorySelector
                currentProject={currentProject}
                linkedJiraProjects={linkedJiraProjects}
                testSuites={testSuites}
                selectedTestSuites={jiraTestSuite}
                onTestSuitesChange={setJiraTestSuite}
                selectedStory={selectedStory}
                onStorySelect={setSelectedStory}
                generateError={jiraGenerateError}
              />
            )
        )}

        {screen === SCREEN.JIRA_REVIEW && (
          <JiraReviewScreen
            tcs={jiraReviewTCs}
            onTcsChange={setJiraReviewTCs}
            technologyLayers={technologyLayers}
            modules={modules}
            labels={labels}
            onEdit={handleEditTc}
            savePhase={savePhase}
            saveProgress={saveProgress}
            saveResult={saveResult}
            existingMatches={existingMatches}
            selectedExisting={selectedExisting}
            onExistingToggle={handleExistingToggle}
            selectedStory={selectedStory}
            tokenUsage={tokenUsage}
          />
        )}

        {screen === SCREEN.FORM && (
          <>
            {(isJiraEditMode || isAiMode) && (
              <>
               {tokenUsage ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.5, mb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Tokens used:
                    </Typography>
                    <Typography  variant="body2"fontWeight={600} color="text.secondary">
                      {tokenUsage.total_tokens.toLocaleString()}
                    </Typography>
                    <Typography  variant="body2" color="text.disabled">
                      ({tokenUsage.prompt_tokens.toLocaleString()} input · {tokenUsage.output_tokens.toLocaleString()} output)
                    </Typography>
                  </Box>
                ) : <Box />}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <AutoAwesomeIcon sx={{ fontSize: 15, color: 'primary.main' }} />
                <Typography variant="caption" color="primary.main" fontWeight={500}>
                  {isJiraEditMode
                    ? 'Editing AI-generated test case — changes are saved when you click Save Changes'
                    : 'Fields pre-filled by AI — review and edit before creating test case'}
                </Typography>
              </Box>
              </>
            )}
            <div className="my-2">
              {!testSuites?.length && (
                <Alert severity="warning">
                  {isAdmin
                    ? 'No test suites found. Please create a test suite to add test cases, as they are required for adding test cases.'
                    : 'No test suites found. Please ask your project admin to create test suites, as they are required for adding test cases.'}
                </Alert>
              )}
            </div>
            {isAiMode && (
              <AiSuggestions
                suggestedLabels={suggestedLabels}
                suggestedModule={suggestedModule}
                suggestedTechnologyLayer={suggestedTechnologyLayer}
                labels={labels}
                modules={modules}
                technologyLayers={technologyLayers}
                onAcceptLabel={handleAcceptLabelChip}
                onDismissLabel={(id) => setSuggestedLabels((prev) => prev.filter((l) => l !== id))}
                onAcceptModule={handleAcceptModuleChip}
                onDismissModule={() => setSuggestedModule(null)}
                onAcceptTechLayer={handleAcceptTechLayerChip}
                onDismissTechLayer={() => setSuggestedTechnologyLayer(null)}
              />
            )}
            <CreateTestCaseForm
              formData={formData}
              isEditing={true}
              setFormData={setFormData}
              defaultFormData={defaultFormData}
              projectUsers={projectUsers}
              statuses={filteredStatuses}
              priorities={priorities}
              testSuites={testSuites}
              labels={labels}
              modules={modules}
              loading={loading}
              isReviewWorkflow={isReviewWorkflow}
              refetchData={refetchData}
              technologyLayers={technologyLayers}
            />
          </>
        )}
      </Modal>

      <InMemoryStepsModal
        show={showStepsModal}
        handleClose={() => setShowStepsModal(false)}
        steps={aiTestSteps}
        onSave={(updatedSteps) => setAiTestSteps(updatedSteps)}
        technologyLayer={currentTechLayerName}
      />

      <ConfirmationModal
        show={showConfirmationModal}
        handleClose={() => setShowConfirmationModal(false)}
        handleConfirm={handleDiscard}
        title="Discard"
        body={confirmationBody}
        buttonLabel="Discard"
        buttonLabelSecondary="Go Back"
        buttonStyle="primary"
      />
    </>
  );
};

export default AddTestCaseModal;